/**
 * As duas conexões do robô, lado a lado (spec 2026-09-24-robo-painel-de-ajustes,
 * 4.5): a Internet e o ConsoleAI. Sem React: recebe o que a tela já leu e devolve
 * o texto, para o teste dizer exatamente o que a pessoa lê.
 *
 * O que separa os dois: um robô sem internet não fala com ninguém, então de longe
 * a Internet é "sem notícia" — quem diz em que etapa ele parou é a USB ou a tela do
 * próprio robô. Já um robô que chega e é recusado (`recusa`) prova que a Internet
 * funciona: o que falhou foi o ConsoleAI aceitar a credencial.
 */
import type { DiagDaRede, MotivoDaRecusa, RecusaDoRobo, RedeSalva } from '@/api/robo'
import { duracao } from './canal'

/** Um pedaço do texto; `mono` é número ou medida (JetBrains Mono, padrão da marca). */
export type Trecho = { t: string; mono?: boolean }

export type DuasConexoes = {
  internet: Trecho[]
  consoleai: Trecho[]
  /** A recusa vale (é mais nova que o último contato aceito). */
  recusado: boolean
}

/** O mesmo critério de "falou há pouco" da rede nos ajustes (spec 3.1). */
export const ATIVO_ATE_S = 120

export const ROTULO_DA_RECUSA: Record<MotivoDaRecusa, string> = {
  credencial: 'credencial',
  revogada: 'credencial revogada',
  sem_escopo: 'sem escopo',
}

export type EntradaDasConexoes = {
  /** `last_seen_at` do cadastro: o último snapshot ACEITO. */
  ultimoContato: string | null
  diag: DiagDaRede | null
  recusa: RecusaDoRobo | null | undefined
  /** As redes que o robô relatou; a `em_uso` dá o nome. */
  redes: RedeSalva[] | null | undefined
  agora: number
}

const texto = (trechos: Trecho[]) => trechos.map((p) => p.t).join('')
export { texto as textoDasConexoes }

export function duasConexoes(e: EntradaDasConexoes): DuasConexoes {
  const ultimo = e.ultimoContato ? Date.parse(e.ultimoContato) : null
  // Recusa mais velha que o último aceito é passado: a credencial voltou a valer.
  const recusa = e.recusa && (ultimo === null || e.recusa.em * 1000 > ultimo) ? e.recusa : null
  const rede = e.redes?.find((r) => r.em_uso)?.ssid ?? null
  const haS = (ms: number) => duracao((e.agora - ms) / 1000)

  if (recusa) {
    return {
      recusado: true,
      // O sinal do diagnóstico NAO entra: a consulta recusada nao grava retrato, e o
      // numero seria o de antes da recusa.
      internet: [{ t: rede ? `Ok, na rede ${rede}` : 'Ok' }],
      consoleai: [
        { t: `Recusou (${ROTULO_DA_RECUSA[recusa.motivo]}) há ` },
        { t: haS(recusa.em * 1000), mono: true },
      ],
    }
  }
  if (ultimo !== null && (e.agora - ultimo) / 1000 < ATIVO_ATE_S) {
    const internet: Trecho[] = [{ t: rede ? rede : 'Conectada' }]
    if (e.diag?.rssi !== undefined) {
      internet.push({ t: ', sinal ' }, { t: `${e.diag.rssi} dBm`, mono: true })
    }
    return { recusado: false, internet, consoleai: [{ t: 'Ativo há ' }, { t: haS(ultimo), mono: true }] }
  }
  return {
    recusado: false,
    internet: [{ t: 'Sem notícia' }],
    consoleai: ultimo === null
      ? [{ t: 'Nunca conectou' }]
      : [{ t: 'Sem contato há ' }, { t: haS(ultimo), mono: true }],
  }
}
